import { useRef, useState } from "react";
import { Clock } from "@fg/icons";
import { Button } from "@/components/ui/button";
import { useSnoozeMessage } from "@/api/hooks";
import { cn } from "@/lib/utils";
import { useDismiss } from "@/lib/useDismiss";
import { useFocusTrap } from "@/lib/useFocusTrap";
import { useStatusAnnouncer } from "@/components/StatusAnnouncer";

interface Preset {
    label: string;
    compute: () => Date;
}

function todayAt(hours: number, minutes = 0): Date {
    const d = new Date();
    d.setHours(hours, minutes, 0, 0);
    return d;
}

function tomorrowAt(hours: number, minutes = 0): Date {
    const d = todayAt(hours, minutes);
    d.setDate(d.getDate() + 1);
    return d;
}

function nextMondayMorning(): Date {
    const d = todayAt(9);
    const day = d.getDay(); // 0 Sun..6 Sat
    const daysUntilMonday = (8 - day) % 7 || 7;
    d.setDate(d.getDate() + daysUntilMonday);
    return d;
}

/** Also offered by the command bar's Snooze menu. */
export const SNOOZE_PRESETS: Preset[] = [
    { label: "Later today (3 PM)", compute: () => todayAt(15) },
    { label: "Tomorrow morning (9 AM)", compute: () => tomorrowAt(9) },
    { label: "Tomorrow evening (6 PM)", compute: () => tomorrowAt(18) },
    { label: "This weekend (Sat 9 AM)", compute: () => {
        const d = todayAt(9);
        const day = d.getDay();
        const daysUntilSat = (6 - day + 7) % 7 || 7;
        d.setDate(d.getDate() + daysUntilSat);
        return d;
    }},
    { label: "Next week (Mon 9 AM)", compute: () => nextMondayMorning() }
];

export function SnoozeMenu({ messageId, triggerClassName }: { messageId: string; triggerClassName?: string }) {
    const snooze = useSnoozeMessage();
    const announce = useStatusAnnouncer().announce;
    const [open, setOpen] = useState(false);
    const [custom, setCustom] = useState(() => {
        const d = tomorrowAt(9);
        // Format to `YYYY-MM-DDTHH:MM` for <input type="datetime-local">
        const pad = (n: number) => n.toString().padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    });
    const ref = useRef<HTMLDivElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);

    useDismiss(ref, () => setOpen(false), open);
    useFocusTrap(popoverRef, open);

    const apply = (when: Date) => {
        snooze.mutate(
            { messageId, wakeAt: when.toISOString() },
            {
                onSuccess: () => {
                    setOpen(false);
                    announce(`Snoozed until ${when.toLocaleString()}`);
                }
            }
        );
    };

    return (
        <div ref={ref} className="relative">
            <Button
                variant="ghost"
                size="icon"
                aria-label="Snooze"
                aria-haspopup="menu"
                aria-expanded={open}
                title="Snooze"
                className={triggerClassName}
                onClick={() => setOpen((v) => !v)}
            >
                <Clock className="h-4 w-4" />
            </Button>
            {open ? (
                <div
                    ref={popoverRef}
                    role="menu"
                    aria-label="Snooze options"
                    className="absolute right-0 top-full z-20 mt-1 w-56 rounded-md border bg-card p-1 shadow-lg"
                >
                    <header className="px-2 py-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Snooze until
                    </header>
                    <ul>
                        {SNOOZE_PRESETS.map((p) => (
                            <li key={p.label}>
                                <button
                                    type="button"
                                    onClick={() => apply(p.compute())}
                                    className={cn(
                                        "flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-xs hover:bg-accent"
                                    )}
                                >
                                    <span>{p.label}</span>
                                    <span className="text-muted-foreground">
                                        {p.compute().toLocaleString(undefined, {
                                            dateStyle: "short",
                                            timeStyle: "short"
                                        })}
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>
                    <div className="mt-1 border-t p-2">
                        <label className="block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            Custom
                        </label>
                        <div className="mt-1 flex gap-1">
                            <input
                                type="datetime-local"
                                value={custom}
                                onChange={(e) => setCustom(e.target.value)}
                                className="h-7 flex-1 rounded-md border px-2 text-xs"
                            />
                            <button
                                type="button"
                                onClick={() => {
                                    const when = new Date(custom);
                                    if (!Number.isNaN(when.getTime())) apply(when);
                                }}
                                className="h-7 rounded-md bg-primary px-2 text-xs text-primary-foreground"
                            >
                                Snooze
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
